import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronRight, ChevronDown, CalendarIcon, Sparkles, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, parse } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { PostLoadCard, PostLoadCardHeader, FieldInput } from "./PostLoadCard";
import { LocationAutocomplete, type PlaceData } from "@/components/ui/location-autocomplete";
import { getGeocodingSuggestions } from "@/lib/mapbox";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const FALLBACK_ORIGINS = ["Memphis, TN", "Chicago, IL", "Dallas, TX", "Atlanta, GA", "Los Angeles, CA"];
const FALLBACK_DESTINATIONS = ["Chicago, IL", "Atlanta, GA", "Dallas, TX", "Miami, FL", "Bronx, NY"];
const HOURS_CHIPS = ["8AM", "10AM", "Noon", "FCFS", "Anytime", "Appointment Required"];

/** Exported for the guided wizard route step — same personalized city chips as the form. */
export function useBrokerCityChips(userId: string | undefined) {
  const [origins, setOrigins] = useState<string[]>(FALLBACK_ORIGINS);
  const [destinations, setDestinations] = useState<string[]>(FALLBACK_DESTINATIONS);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const fetchOrigins = supabase
      .from("loads")
      .select("origin_city, origin_state")
      .eq("created_by", userId)
      .limit(500);

    const fetchDestinations = supabase
      .from("loads")
      .select("destination_city, destination_state")
      .eq("created_by", userId)
      .limit(500);

    Promise.all([fetchOrigins, fetchDestinations]).then(([oRes, dRes]) => {
      if (cancelled) return;

      if (oRes.data && oRes.data.length >= 3) {
        const counts: Record<string, number> = {};
        for (const r of oRes.data) {
          const key = `${r.origin_city}, ${r.origin_state}`;
          counts[key] = (counts[key] || 0) + 1;
        }
        const top = Object.entries(counts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([k]) => k);
        if (top.length >= 3) setOrigins(top);
      }

      if (dRes.data && dRes.data.length >= 3) {
        const counts: Record<string, number> = {};
        for (const r of dRes.data) {
          const key = `${r.destination_city}, ${r.destination_state}`;
          counts[key] = (counts[key] || 0) + 1;
        }
        const top = Object.entries(counts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([k]) => k);
        if (top.length >= 3) setDestinations(top);
      }
    });

    return () => { cancelled = true; };
  }, [userId]);

  return { origins, destinations };
}

/** Resolve a "City, ST" string to full PlaceData using local DB first, Mapbox fallback */
async function resolveChipLocation(label: string): Promise<{ displayLabel: string; placeData: PlaceData } | null> {
  try {
    // Try local DB first via supabase RPC
    const { supabase } = await import("@/integrations/supabase/client");
    const { data: localResults } = await supabase.rpc('search_locations', { q: label, lim: 1 });
    if (localResults && localResults.length > 0) {
      const r = localResults[0] as any;
      const placeData: PlaceData = {
        name: `${r.city}, ${r.state_code}`,
        address: `${r.city}, ${r.state_code}`,
        city: r.city,
        state: r.state_code,
        country: 'US',
        postal_code: '',
        latitude: r.lat || 0,
        longitude: r.lon || 0,
      };
      return { displayLabel: `${r.city}, ${r.state_code}`, placeData };
    }
    // Mapbox fallback
    const data = await getGeocodingSuggestions(label);
    const features = data?.features;
    if (!features || features.length === 0) return null;
    const first = features[0];
    const [lng, lat] = first.center || [0, 0];
    const cityCtx = first.context?.find((c: any) => c.id?.startsWith('place'));
    const stateCtx = first.context?.find((c: any) => c.id?.startsWith('region'));
    const city = cityCtx?.text || first.text || '';
    const state = (stateCtx?.short_code || stateCtx?.text || '').replace('US-', '');
    const placeData: PlaceData = {
      name: first.place_name,
      address: first.place_name,
      city: city || first.text,
      state,
      country: 'US',
      postal_code: '',
      latitude: lat,
      longitude: lng,
    };
    const displayLabel = city && state ? `${city}, ${state}` : first.place_name;
    return { displayLabel, placeData };
  } catch {
    return null;
  }
}

/** Exported for the guided wizard route step — same chip behavior as the form. */
export function LocationChips({
  suggestions,
  currentValue,
  onSelect,
  onClear,
}: {
  suggestions: string[];
  currentValue: string;
  onSelect: (label: string, placeData: PlaceData) => void;
  onClear: () => void;
}) {
  const [resolving, setResolving] = useState<string | null>(null);

  const handleClick = useCallback(async (chip: string) => {
    // If already active, clear
    if (currentValue === chip) {
      onClear();
      return;
    }
    setResolving(chip);
    const result = await resolveChipLocation(chip);
    setResolving(null);
    if (result) {
      onSelect(result.displayLabel, result.placeData);
    }
  }, [currentValue, onSelect, onClear]);

  return (
    <div className="flex items-center gap-1 flex-wrap pt-1">
      {suggestions.map((s) => {
        const isActive = currentValue === s;
        const isLoading = resolving === s;
        return (
          <button
            key={s}
            type="button"
            disabled={isLoading}
            onClick={() => handleClick(s)}
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-medium transition-colors duration-150 border",
              isActive
                ? "border-primary bg-primary/10 text-primary"
                : "border-border/40 bg-transparent text-muted-foreground hover:bg-muted/50",
              isLoading && "opacity-50 cursor-wait"
            )}
          >
            {s}
          </button>
        );
      })}
    </div>
  );
}

function QuickFillChips({ onSelect }: { onSelect: (v: string) => void }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap pt-1">
      {HOURS_CHIPS.map((chip) => (
        <button
          key={chip}
          type="button"
          onClick={() => onSelect(chip)}
          className="inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-medium transition-all duration-150"
          style={{
            border: "1px solid hsl(170 85% 32% / 0.4)",
            color: "hsl(var(--brand-teal-deep))",
            backgroundColor: "hsl(170 85% 32% / 0.1)",
          }}
        >
          {chip}
        </button>
      ))}
    </div>
  );
}

const FALLBACK_WINDOWS = ["8–9 AM", "9–10 AM", "10–11 AM", "11 AM–12 PM", "12–1 PM", "1–2 PM"];

function formatHourWindow(hour: number): string {
  const fmt = (h: number) => {
    if (h === 0 || h === 24) return "12 AM";
    if (h === 12) return "12 PM";
    return h > 12 ? `${h - 12} PM` : `${h} AM`;
  };
  return `${fmt(hour)}–${fmt(hour + 2)}`;
}

function getDeterministicFallback(oCity: string, oState: string, dCity: string, dState: string): string {
  const now = new Date();
  const doy = Math.floor((now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / 86400000);
  const hash = (oCity + oState + dCity + dState).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return FALLBACK_WINDOWS[(doy + hash) % FALLBACK_WINDOWS.length];
}

function useLanePostingInsight(oCity?: string, oState?: string, dCity?: string, dState?: string): string | null {
  const [insight, setInsight] = useState<string | null>(null);
  const lastKeyRef = useRef("");

  useEffect(() => {
    if (!oState || !dState) { setInsight(null); return; }
    const key = `${oCity || ""}|${oState}|${dCity || ""}|${dState}`;
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;

    let cancelled = false;

    (async () => {
      let query = supabase
        .from("loads")
        .select("created_at")
        .eq("status", "posted")
        .order("created_at", { ascending: false })
        .limit(200);

      if (oCity && dCity) {
        query = query.eq("origin_city", oCity).eq("origin_state", oState)
          .eq("destination_city", dCity).eq("destination_state", dState);
      } else {
        query = query.eq("origin_state", oState).eq("destination_state", dState);
      }

      const { data } = await query;
      if (cancelled) return;

      let rows = data || [];
      if (rows.length < 3 && oCity && dCity) {
        const { data: stateData } = await supabase
          .from("loads")
          .select("created_at")
          .eq("origin_state", oState)
          .eq("destination_state", dState)
          .eq("status", "posted")
          .order("created_at", { ascending: false })
          .limit(200);
        if (cancelled) return;
        rows = stateData || [];
      }

      if (rows.length < 3) {
        setInsight(`Peak posting time for this lane: ${getDeterministicFallback(oCity || "", oState, dCity || "", dState)}`);
        return;
      }

      const hourCounts: Record<number, number> = {};
      for (const r of rows) {
        const h = new Date(r.created_at).getHours();
        hourCounts[h] = (hourCounts[h] || 0) + 1;
      }
      const peakHour = Object.entries(hourCounts).sort((a, b) => b[1] - a[1])[0][0];
      setInsight(`Peak posting time for this lane: ${formatHourWindow(Number(peakHour))}`);
    })();

    return () => { cancelled = true; };
  }, [oCity, oState, dCity, dState]);

  return insight;
}

function AIHelperText({ visible, text }: { visible: boolean; text: string }) {
  return (
    <p
      className="text-[10px] font-medium transition-all duration-200 overflow-hidden"
      style={{
        color: "hsl(var(--brand-teal-deep))",
        opacity: visible ? 1 : 0,
        maxHeight: visible ? 20 : 0,
        transform: visible ? "translateY(0)" : "translateY(-4px)",
      }}
    >
      <Sparkles className="inline h-2.5 w-2.5 mr-1" style={{ verticalAlign: "-2px" }} />
      {text}
    </p>
  );
}

function DatePickerField({
  label,
  value,
  onChange,
  clearable = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  clearable?: boolean;
}) {
  const dateObj = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const validDate = dateObj && !isNaN(dateObj.getTime()) ? dateObj : undefined;

  return (
    <div className="relative flex items-center">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "h-9 w-full justify-start text-left text-xs font-normal",
              clearable && validDate && "pr-7",
              !validDate && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-3 w-3 text-muted-foreground" />
            {validDate ? format(validDate, "MM/dd/yyyy") : label}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={validDate}
            onSelect={(d) => {
              if (d) onChange(format(d, "yyyy-MM-dd"));
            }}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        </PopoverContent>
      </Popover>
      {clearable && validDate && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-2 rounded-full p-0.5 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Clear date"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

interface ShipmentDetailsCardProps {
  origin: string;
  destination: string;
  pickupEarliest: string;
  deliveryDate: string;
  pickHrs: string;
  dropHrs: string;
  originCity?: string;
  originState?: string;
  destinationCity?: string;
  destinationState?: string;
  onOriginChange: (value: string, placeData?: PlaceData) => void;
  onDestinationChange: (value: string, placeData?: PlaceData) => void;
  onPickupEarliestChange: (v: string) => void;
  onDeliveryDateChange: (v: string) => void;
  /** Bid loads require a delivery date; quotes do not. */
  deliveryDateRequired?: boolean;
  onPickHrsChange: (v: string) => void;
  onDropHrsChange: (v: string) => void;
  errors?: Record<string, string>;
}

export default function ShipmentDetailsCard({
  origin,
  destination,
  pickupEarliest,
  deliveryDate,
  pickHrs,
  dropHrs,
  originCity,
  originState,
  destinationCity,
  destinationState,
  onOriginChange,
  onDestinationChange,
  onPickupEarliestChange,
  onDeliveryDateChange,
  deliveryDateRequired = false,
  onPickHrsChange,
  onDropHrsChange,
  errors,
}: ShipmentDetailsCardProps) {
  const { user } = useAuth();
  const [pickFocused, setPickFocused] = useState(false);
  const [dropFocused, setDropFocused] = useState(false);
  const { origins: originChips, destinations: destChips } = useBrokerCityChips(user?.id);
  const laneInsight = useLanePostingInsight(originCity, originState, destinationCity, destinationState);

  return (
    <PostLoadCard variant="console">
      <PostLoadCardHeader
        icon={<ChevronRight className="h-3.5 w-3.5" style={{ color: "hsl(var(--brand-teal-deep))" }} />}
        title="Shipment Details"
        demoHintKey="section.shipmentDetails"
      >
        <div className="flex items-center gap-1.5 text-[10px]">
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "hsl(var(--success))" }} />
          <span className="font-medium text-muted-foreground">Draft</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </div>
      </PostLoadCardHeader>

      <div className="px-3 pb-3 pt-2 space-y-2">
        {/* Origin / Destination */}
        <div className="grid grid-cols-2 gap-2 pt-1">
          <div>
            <label className="text-[10px] font-medium text-muted-foreground mb-1 block">
              Origin <span className="text-destructive">*</span>
            </label>
            <LocationAutocomplete
              value={origin}
              onChange={(val, pd) => onOriginChange(val, pd)}
              placeholder="City, ST or ZIP"
              className={`h-9 text-xs ring-offset-0 ${errors?.origin ? "border-destructive" : ""}`}
            />
            {errors?.origin && <p className="text-[10px] text-destructive mt-0.5">{errors.origin}</p>}
            <div className="relative z-0">
              <LocationChips
                suggestions={originChips}
                currentValue={origin}
                onSelect={(label, pd) => onOriginChange(label, pd)}
                onClear={() => onOriginChange("")}
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-medium text-muted-foreground mb-1 block">
              Destination <span className="text-destructive">*</span>
            </label>
            <LocationAutocomplete
              value={destination}
              onChange={(val, pd) => onDestinationChange(val, pd)}
              placeholder="City, ST or ZIP"
              className={`h-9 text-xs ring-offset-0 ${errors?.destination ? "border-destructive" : ""}`}
            />
            {errors?.destination && <p className="text-[10px] text-destructive mt-0.5">{errors.destination}</p>}
            <div className="relative z-0">
              <LocationChips
                suggestions={destChips}
                currentValue={destination}
                onSelect={(label, pd) => onDestinationChange(label, pd)}
                onClear={() => onDestinationChange("")}
              />
            </div>
          </div>
        </div>

        {/* Pickup Earliest / Delivery Date */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-medium text-muted-foreground mb-1 block">Pickup Earliest <span className="text-destructive">*</span></label>
            <DatePickerField
              label="Pickup Earliest"
              value={pickupEarliest}
              onChange={onPickupEarliestChange}
            />
            {errors?.pickupEarliest && <p className="text-[10px] text-destructive mt-0.5">{errors.pickupEarliest}</p>}
          </div>
          <div>
            <label className="text-[10px] font-medium text-muted-foreground mb-1 block">
              Delivery Date{deliveryDateRequired && <span className="text-destructive"> *</span>}
            </label>
            <DatePickerField
              label="Delivery Date"
              value={deliveryDate}
              onChange={onDeliveryDateChange}
              clearable
            />
            {errors?.deliveryDate && <p className="text-[10px] text-destructive mt-0.5">{errors.deliveryDate}</p>}
          </div>
        </div>

        {/* Pick Hrs / Drop Hrs */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-medium text-muted-foreground mb-1 block">
              Pickup time <span className="text-muted-foreground/70">(examples: 10AM, 9-12, FCFS, APPT 2PM)</span>
            </label>
            <FieldInput
              label="Pickup time"
              value={pickHrs}
              onChange={onPickHrsChange}
              placeholder=""
              onFocus={() => setPickFocused(true)}
              onBlur={() => setTimeout(() => setPickFocused(false), 150)}
            />
            <p className="text-[10px] text-muted-foreground mt-1">Add your pickup time here</p>
            {pickFocused && <QuickFillChips onSelect={onPickHrsChange} />}
          </div>
          <div>
            <label className="text-[10px] font-medium text-muted-foreground mb-1 block">
              Delivery time <span className="text-muted-foreground/70">(examples: 10AM, 9-12, FCFS, APPT 2PM)</span>
            </label>
            <FieldInput
              label="Delivery time"
              value={dropHrs}
              onChange={onDropHrsChange}
              placeholder=""
              onFocus={() => setDropFocused(true)}
              onBlur={() => setTimeout(() => setDropFocused(false), 150)}
            />
            <p className="text-[10px] text-muted-foreground mt-1">Add delivery time here</p>
            {dropFocused && <QuickFillChips onSelect={onDropHrsChange} />}
          </div>
        </div>
      </div>
    </PostLoadCard>
  );
}
